/**
 * Lightweight localStorage state for the Lemonade Leaders Jr. demo.
 *
 * No backend, no auth — by design (per pilot brief). We keep the surface
 * tiny so the future API migration is trivial: just swap these helpers.
 */
const KEY = "lemonade_jr_state_v1";

const DEFAULT_STATE = {
  problem: "",          // free-text from Mission 1
  bizBird: null,        // "eagle" | "parrot" | "dove" | "owl"
  businessIdea: "",     // chosen problem from Mission 3 (or custom text)
  problemChoice: "",    // option key selected on Mission 3
  customProblem: "",    // text when "I have my own idea"
  customer: "",         // populated by Mission 4 (who the business helps)
  helpKind: "",         // populated by Mission 4 step 2 — what kind of help
  // Mission 5 — Brand Your Business
  brandName: "",
  brandColor: "",       // "sunny" | "sky" | "berry" | "mint" | "grape" | "orange"
  brandShape: "",       // "lemon" | "star" | "heart" | "cloud"
  // Mission 6 — Money Mission (Coin Counter)
  coinGameBestRounds: 0,// best rounds completed in one run (0-3)
  coinGameUsedHelper: false,
  // Mission 7 — Tell the World (Sign Designer)
  signSlogan: "",
  signBg: "",           // color-token
  signAccent: "",
  studentName: "",      // populated by Mission 9 (Graduation Hall)
  pitch: "",            // populated by Mission 8
  completedMissions: [], // [1, 2, 3, ...]
  customizedMissions: [], // missions where the student wrote their own answer (drives 2-star rating)
};

/** Sample state used by the "Try Full Demo Flow" button. Read-only — we
 *  spread it into `saveJrState` to preserve future fields. */
export const SAMPLE_COMPLETED_STATE = {
  problem: "Kids lose pencils.",
  bizBird: "eagle",
  problemChoice: "pencils",
  customProblem: "",
  businessIdea: "Pencil Power Station",
  customer: "Students who need pencils.",
  helpKind: "Solve a small problem",
  brandName: "Pencil Power",
  brandColor: "sunny",
  brandShape: "lemon",
  coinGameBestRounds: 3,
  coinGameUsedHelper: false,
  signSlogan: "Always Ready to Learn!",
  signBg: "sunny",
  signAccent: "berry",
  studentName: "Junior CEO",
  pitch:
    "Pencil Power Station helps students stay ready to learn by giving them a quick place to get a pencil when they need one.",
  completedMissions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
  // Demo state assumes a confident Junior CEO who customized everywhere:
  customizedMissions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
};

export function loadJrState() {
  if (typeof window === "undefined") return { ...DEFAULT_STATE };
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_STATE };
    return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

export function saveJrState(patch) {
  if (typeof window === "undefined") return;
  try {
    const next = { ...loadJrState(), ...patch };
    window.localStorage.setItem(KEY, JSON.stringify(next));
    return next;
  } catch {
    /* ignore quota/availability errors silently — demo only */
  }
}

export function markMissionComplete(num, { customized = false } = {}) {
  const cur = loadJrState();
  const completedMissions = cur.completedMissions.includes(num)
    ? cur.completedMissions
    : [...cur.completedMissions, num].sort((a, b) => a - b);
  let customizedMissions = cur.customizedMissions || [];
  if (customized && !customizedMissions.includes(num)) {
    customizedMissions = [...customizedMissions, num].sort((a, b) => a - b);
  } else if (!customized && customizedMissions.includes(num)) {
    customizedMissions = customizedMissions.filter((x) => x !== num);
  }
  return saveJrState({ completedMissions, customizedMissions });
}

export function resetJrState() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export const JR_BIRDS = {
  eagle: {
    emoji: "🦅",
    image: "/elementary/birds/eagle.jpg",
    name: "Eagle",
    role: "Leader",
    description: "The brave leader who helps the team make decisions.",
  },
  parrot: {
    emoji: "🦜",
    image: "/elementary/birds/parrot.jpg",
    name: "Parrot",
    role: "Speaker",
    description: "The speaker and promoter who helps share the idea.",
  },
  dove: {
    emoji: "🕊️",
    image: "/elementary/birds/dove.jpg",
    name: "Dove",
    role: "Helper",
    description: "The helper and encourager who keeps the team working together.",
  },
  owl: {
    emoji: "🦉",
    image: "/elementary/birds/owl.jpg",
    name: "Owl",
    role: "Planner",
    description: "The planner and thinker who checks the details.",
  },
};

export const JR_PROBLEM_OPTIONS = [
  { key: "supplies", label: "Students forget supplies", icon: "backpack" },
  { key: "reminders", label: "People need reminders", icon: "bell" },
  { key: "encouragement", label: "Kids need encouragement", icon: "heart" },
  { key: "organize", label: "The classroom needs organizing", icon: "clipboard" },
  { key: "new-friends", label: "New students need friends", icon: "user-plus" },
  { key: "custom", label: "I have my own idea", icon: "sparkles" },
  // Legacy keys kept so older localStorage saves still resolve a label.
  { key: "pencils", label: "Kids lose pencils", icon: "pencil", legacy: true },
  { key: "messy", label: "Classrooms get messy", icon: "trash", legacy: true },
  { key: "friends", label: "Students need help making friends", icon: "user-plus", legacy: true },
];

export const JR_MISSION1_PROBLEMS = [
  { key: "pencils", label: "Kids lose pencils", icon: "pencil", color: "yellow" },
  { key: "messy", label: "Classrooms get messy", icon: "trash", color: "orange" },
  { key: "encouragement", label: "Friends need encouragement", icon: "heart", color: "rose" },
  { key: "friends", label: "Students need help making friends", icon: "user-plus", color: "sky" },
  { key: "lunch", label: "Lunch lines feel too long", icon: "clock", color: "amber" },
  { key: "custom", label: "I have my own idea", icon: "sparkles", color: "violet" },
];

export const JR_CUSTOMER_OPTIONS = [
  { key: "students", label: "Students", icon: "graduation-cap", color: "yellow" },
  { key: "teachers", label: "Teachers", icon: "users", color: "orange" },
  { key: "families", label: "Families", icon: "home", color: "rose" },
  { key: "friends", label: "Friends", icon: "smile", color: "sky" },
  { key: "neighbors", label: "Neighbors", icon: "map-pin", color: "emerald" },
  { key: "pets", label: "Pets", icon: "paw", color: "amber" },
  { key: "custom", label: "My own customer", icon: "sparkles", color: "violet" },
];

export const JR_HELP_KINDS = [
  { key: "save-time", label: "Save time", icon: "clock" },
  { key: "smile", label: "Make people smile", icon: "smile" },
  { key: "learn", label: "Help people learn", icon: "book" },
  { key: "organize", label: "Keep things organized", icon: "clipboard" },
  { key: "solve", label: "Solve a small problem", icon: "lightbulb" },
  { key: "encourage", label: "Encourage others", icon: "heart" },
];

export const JR_PITCH_CHIPS = {
  who: [
    "students",
    "teachers",
    "families",
    "friends",
    "classmates",
  ],
  how: [
    "giving them pencils",
    "sharing kindness cards",
    "helping them stay organized",
    "reminding them what to do",
    "making them smile",
  ],
  why: [
    "learning matters",
    "kindness matters",
    "everyone needs help sometimes",
    "no one should feel left out",
    "small ideas can make a big difference",
  ],
};

export const JR_BIRD_CONFIRMATIONS = {
  eagle: "You chose Eagle! Every team needs brave leaders.",
  parrot: "You chose Parrot! Every team needs great speakers.",
  dove: "You chose Dove! Every team needs kind helpers.",
  owl: "You chose Owl! Every team needs careful planners.",
};

export const JR_BRAND_COLORS = [
  { key: "sunny",  name: "Sunny Yellow", bg: "bg-yellow-400",  ring: "ring-yellow-500", text: "text-yellow-900", hex: "#facc15" },
  { key: "sky",    name: "Sky Blue",     bg: "bg-sky-400",     ring: "ring-sky-500",    text: "text-sky-900",    hex: "#38bdf8" },
  { key: "berry",  name: "Berry Pink",   bg: "bg-rose-400",    ring: "ring-rose-500",   text: "text-rose-900",   hex: "#fb7185" },
  { key: "mint",   name: "Mint Green",   bg: "bg-emerald-400", ring: "ring-emerald-500",text: "text-emerald-900",hex: "#34d399" },
  { key: "grape",  name: "Grape Purple", bg: "bg-violet-400",  ring: "ring-violet-500", text: "text-violet-900", hex: "#a78bfa" },
  { key: "orange", name: "Zesty Orange", bg: "bg-orange-400",  ring: "ring-orange-500", text: "text-orange-900", hex: "#fb923c" },
];

export const JR_BRAND_SHAPES = [
  { key: "lemon", name: "Lemon",  emoji: "🍋" },
  { key: "star",  name: "Star",   emoji: "⭐" },
  { key: "heart", name: "Heart",  emoji: "❤️" },
  { key: "cloud", name: "Cloud",  emoji: "☁️" },
];

export const JR_SIGN_SLOGANS = [
  "Always Ready to Learn!",
  "Big Ideas, Little CEOs!",
  "Kindness Is My Business!",
  "Helping One Friend at a Time!",
  "Small Ideas, Big Smiles!",
  "The Future Starts Here!",
];
